import { InfoCircleOutlined } from "@ant-design/icons"
import { Alert, Button, Card, Flex, Popover, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { type TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import { type LeadEducationResult } from "./hooks/use-lead-education.ts"
import { formatEducationFieldValue } from "./format-education-field-value.ts"
import { ProjectTypeConfirm, type ProjectTypeConfirmSubmission } from "./ProjectTypeConfirm.tsx"
import type {
  EducationProjectTypeRef,
  LeadEducationFlags,
  LeadEducationInputs,
  LeadEducationPermissions,
  StartGenerationData
} from "./types/education.types.ts"

const { Title, Text } = Typography

interface EducationEmptyStateProps {
  inputs: LeadEducationInputs
  flags: LeadEducationFlags
  permissions: LeadEducationPermissions
  canEdit: boolean
  onGenerate: (body: StartGenerationData) => Promise<LeadEducationResult>
  onGoToTab: (key: string) => void
  onReviewInputs: () => void
}

const lowerFirst = (value: string) => value.charAt(0).toLowerCase() + value.slice(1)

const joinWithAnd = (items: string[], t: TFunction): string => {
  if (items.length <= 1) return items[0] ?? ""
  return t("projectEducation.emptyState.listAnd", {
    head: items.slice(0, -1).join(", "),
    last: items[items.length - 1]
  })
}

/**
 * Screen 1 — the lead's Project Education tab before anything has been
 * generated. Owns the project-type confirmation flow (proactive when the
 * lead has several types, reactive on a 422 `PROJECT_TYPE_REQUIRED`) since
 * both feed the same "Generate both guides" action; the Review Inputs
 * drawer itself is owned by `LeadEducationTab` so a later stale-fields
 * banner (Task 4) can open the identical drawer.
 */
export function EducationEmptyState({
  inputs,
  flags,
  permissions,
  canEdit,
  onGenerate,
  onGoToTab,
  onReviewInputs
}: EducationEmptyStateProps) {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmChoices, setConfirmChoices] = useState<EducationProjectTypeRef[]>([])
  const [generateError, setGenerateError] = useState<string | null>(null)

  const fieldByKey = useMemo(
    () => Object.fromEntries(inputs.fields.map((field) => [field.key, field])),
    [inputs.fields]
  )

  const hasProjectType = inputs.project_types.length > 0
  const canShowGenerate = canEdit && permissions.can_generate

  const missingLabels = inputs.missing
    .map((key) => fieldByKey[key]?.label)
    .filter((label): label is string => !!label)

  // Only the "no customer generation" flag changes the request body — the
  // single-project-type happy path must send a bare `{}`.
  const withAudiencesPatch = (body: StartGenerationData): StartGenerationData =>
    flags.customer_generation ? body : { ...body, audiences: ["sales_person"] }

  const runGenerate = async (body: StartGenerationData) => {
    setGenerateError(null)
    setSubmitting(true)
    const result = await onGenerate(withAudiencesPatch(body))
    setSubmitting(false)

    if (result.ok) {
      setConfirmOpen(false)
      return
    }
    if (result.error.code === "PROJECT_TYPE_REQUIRED" && result.error.choices?.length) {
      setConfirmChoices(result.error.choices)
      setConfirmOpen(true)
      return
    }
    setGenerateError(result.error.message)
  }

  const handleGenerateClick = () => {
    if (inputs.project_types.length > 1) {
      setGenerateError(null)
      setConfirmChoices(inputs.project_types)
      setConfirmOpen(true)
      return
    }
    void runGenerate({})
  }

  const handleConfirm = (body: ProjectTypeConfirmSubmission) => {
    void runGenerate(body)
  }

  const renderSummaryRow = (
    labelKey: string,
    fieldKey: string,
    options?: { withAdd?: boolean }
  ) => {
    const field = fieldByKey[fieldKey]
    const formatted = field ? formatEducationFieldValue(field.value, t) : null

    return (
      <Flex
        key={fieldKey}
        justify={"space-between"}
        align={"center"}
        className={"border-b border-border py-2 last:border-b-0"}
      >
        <Text type={"secondary"}>{t(labelKey)}</Text>
        <Flex align={"center"} gap={8}>
          <Text>{formatted ?? t("projectEducation.emptyState.notProvided")}</Text>
          {options?.withAdd && !formatted && canEdit && (
            <Button type={"link"} size={"small"} onClick={() => onGoToTab("additional")}>
              {t("projectEducation.emptyState.add")}
            </Button>
          )}
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={"middle"} data-testid={"education-empty-state"}>
      <Flex justify={"space-between"} align={"center"}>
        <Title level={4} className={"m-0"}>
          {t("projectEducation.header.title")}
        </Title>
        <Popover
          trigger={"click"}
          content={
            <div className={"max-w-xs text-sm"}>
              {t("projectEducation.header.howThisWorksBody")}
            </div>
          }
        >
          <Button type={"link"} icon={<InfoCircleOutlined />}>
            {t("projectEducation.header.howThisWorks")}
          </Button>
        </Popover>
      </Flex>

      <Text type={"secondary"}>{t("projectEducation.emptyState.explainer")}</Text>

      <Card size={"small"}>
        <Flex vertical>
          {renderSummaryRow("projectEducation.emptyState.fields.projectType", "project_type")}
          {renderSummaryRow("projectEducation.emptyState.fields.location", "location")}
          {renderSummaryRow("projectEducation.emptyState.fields.timeline", "timeline")}
          {renderSummaryRow("projectEducation.emptyState.fields.budget", "budget_range", {
            withAdd: true
          })}
          {renderSummaryRow(
            "projectEducation.emptyState.fields.scopeNotes",
            "detailed_project_description"
          )}
        </Flex>
        <Flex justify={"end"} className={"pt-2"}>
          <Button onClick={onReviewInputs}>
            {t("projectEducation.emptyState.reviewInputs")}
          </Button>
        </Flex>
      </Card>

      {missingLabels.length > 0 && (
        // Product rule: missing lead data never blocks or discourages
        // generation — the AI generates the best it can under the rules,
        // and the data only helps it tailor. A neutral/info tip, not a
        // warning: Generate itself is disabled only for a missing project
        // type (see `hasProjectType` below), never for this.
        <Alert
          type={"info"}
          showIcon
          data-testid={"optional-data-tip"}
          message={t("projectEducation.emptyState.optionalDataTip", {
            fields: joinWithAnd(missingLabels.map(lowerFirst), t)
          })}
        />
      )}

      <Flex gap={"middle"} wrap>
        <Card size={"small"} className={"flex-1"} title={t("projectEducation.tracks.salesPerson.title")}>
          <Text type={"secondary"}>{t("projectEducation.tracks.salesPerson.subtitle")}</Text>
        </Card>
        <Card
          size={"small"}
          className={"flex-1"}
          title={t("projectEducation.tracks.customer.title")}
        >
          <Text type={"secondary"}>
            {flags.customer_generation
              ? t("projectEducation.tracks.customer.subtitleEnabled")
              : t("projectEducation.tracks.customer.subtitleDisabled")}
          </Text>
        </Card>
      </Flex>

      {generateError && <Alert type={"error"} showIcon message={generateError} />}

      {canShowGenerate ? (
        <Flex vertical gap={4}>
          <Button
            type={"primary"}
            loading={submitting}
            disabled={!hasProjectType}
            onClick={handleGenerateClick}
          >
            {t("projectEducation.emptyState.generate")}
          </Button>
          {!hasProjectType && (
            <Text type={"secondary"} className={"text-xs"}>
              {t("projectEducation.emptyState.noProjectType")}
            </Text>
          )}
          <Text type={"secondary"} className={"text-xs"}>
            {t("projectEducation.emptyState.generateFootnote")}
          </Text>
        </Flex>
      ) : (
        <Text type={"secondary"}>{t("projectEducation.emptyState.noEditAccess")}</Text>
      )}

      <ProjectTypeConfirm
        open={confirmOpen}
        choices={confirmChoices}
        submitting={submitting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </Flex>
  )
}
